/**
 * Landing-attribution capture for the signup funnel (OCP-176).
 *
 * Runs ONCE per browser session (not per page-view). Captures:
 *   - referrer DOMAIN (no path, no query — privacy)
 *   - landing PATH (no host, no query)
 *   - manual `?ref=` campaign tag
 *
 * Persists to sessionStorage so a customer who lands on /mail, clicks
 * around, then eventually hits /signup still attributes to the
 * original arrival source. Cleared automatically by the browser when
 * the tab closes — no consent needed (not a cookie, not persistent).
 *
 * The signup POST body picks these up in SignupPage. Backend stores
 * them on portal_customers.signup_referrer / signup_landing_path /
 * signup_ref (OCP-176 backend, migration 0039).
 */

const STORAGE_KEY = 'orboto_landing_attribution_v1';

export interface CapturedLanding {
  /** Domain only — 'npmjs.com', 'orboto.io', or '(direct)' when document.referrer is empty / same-origin. */
  referrer: string;
  /** URL path the customer first landed on. Starts with /. */
  landing_path: string;
  /** Manual ?ref=xxx campaign tag. Validated alphanumeric + dash/underscore. */
  ref: string | null;
}

/**
 * Capture (or no-op if already captured). Safe to call multiple times
 * — only the first invocation per session writes to storage.
 */
export function captureLandingOnce(): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;
  } catch {
    // Some embedded contexts (private-mode, iframe) throw on access —
    // bail silently; capture is best-effort.
    return;
  }

  const captured: CapturedLanding = {
    referrer: extractDomain(document.referrer),
    landing_path: sanitizePath(window.location.pathname),
    ref: extractRefParam(window.location.search),
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Storage quota / private-mode — silent skip.
  }
}

/**
 * Read what was captured at first page-load. Returns null when nothing
 * was captured (e.g. SSR, private mode, sessionStorage cleared).
 */
export function getCapturedLanding(): CapturedLanding | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CapturedLanding>;
    if (typeof parsed.referrer !== 'string') return null;
    if (typeof parsed.landing_path !== 'string') return null;
    return {
      referrer: parsed.referrer,
      landing_path: parsed.landing_path,
      ref: typeof parsed.ref === 'string' ? parsed.ref : null,
    };
  } catch {
    return null;
  }
}

/**
 * Extract the hostname from a referrer URL, stripping `www.` prefix.
 * Returns '(direct)' for empty / unparseable / same-origin referrers.
 *
 * Examples:
 *   'https://www.npmjs.com/package/@orboto/mail' → 'npmjs.com'
 *   'https://orboto.io/pricing'                  → 'orboto.io'
 *   '' (direct nav, paste-URL, bookmark)         → '(direct)'
 *   'https://account.orboto.io/products'         → '(direct)'  (same-site)
 */
function extractDomain(referrer: string): string {
  if (!referrer) return '(direct)';
  try {
    const url = new URL(referrer);
    const host = url.hostname.toLowerCase();
    // Same-site referrer → treat as direct so internal nav doesn't
    // pollute the attribution data.
    if (typeof window !== 'undefined' && host === window.location.hostname) {
      return '(direct)';
    }
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    return '(direct)';
  }
}

/**
 * Sanitize the landing path: must start with /, strip query + fragment,
 * cap at 512 chars (matches backend Zod max).
 */
function sanitizePath(pathname: string): string {
  if (!pathname || !pathname.startsWith('/')) return '/';
  // pathname is already query-stripped on most browsers but be defensive.
  const noQuery = pathname.split('?')[0]!.split('#')[0]!;
  return noQuery.slice(0, 512);
}

/**
 * Extract + validate `?ref=` query param. Alphanumeric + dash/underscore
 * only (matches backend Zod regex). Returns null when absent or
 * unsafe.
 */
function extractRefParam(search: string): string | null {
  try {
    const params = new URLSearchParams(search);
    const v = params.get('ref');
    if (!v) return null;
    if (!/^[a-z0-9_-]{1,64}$/i.test(v)) return null;
    return v;
  } catch {
    return null;
  }
}
